import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #index = 0;
  #opener;
  #listeners;
  #resizeObserver = new ResizeObserver(() => this.#scrollThumbnail());
  #highResImagesLoaded = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.refs.dialog.addEventListener('cancel', this.#cancel, options);
    this.refs.dialog.addEventListener('close', this.#afterClose, options);
    this.addEventListener('mdc-zoom-change', this.#updateControls, options);
    this.addEventListener('mdc-gallery-step', this.#step, options);
    this.#resizeObserver.observe(this.refs.thumbnails);
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#resizeObserver.disconnect();
    this.#pauseMedia();
    super.disconnectedCallback();
  }

  open(index, event) {
    event?.preventDefault();
    this.#opener = document.activeElement;
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.selectThumbnail(Number(index));
    this.querySelector('[data-gallery-close]')?.focus({ preventScroll: true });
  }

  close() {
    this.refs.dialog.close();
  }

  closeDialog() {
    this.close();
  }

  #cancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #afterClose = () => {
    this.#pauseMedia();
    this.#wrapper?.reset?.();
    window.dispatchEvent(new DialogCloseEvent());
    if (this.#opener instanceof HTMLElement && this.#opener.isConnected) {
      this.#opener.focus({ preventScroll: true });
    }
  };

  #pauseMedia() {
    this.querySelectorAll('deferred-media, product-model').forEach((media) => media.pauseMedia?.());
    this.querySelectorAll('video').forEach((video) => video.pause());
  }

  get #wrapper() {
    return this.refs.media[this.#index]?.querySelector('drag-zoom-wrapper');
  }

  handleKeyDown(event) {
    const target = event.composedPath?.()[0] ?? event.target;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (target.closest('video, model-viewer, input, select, textarea')) return;
    const actions = {
      ArrowLeft: () => this.previous(),
      ArrowRight: () => this.next(),
      '+': () => this.zoomIn(),
      '=': () => this.zoomIn(),
      '-': () => this.zoomOut(),
      '0': () => this.resetZoom(),
    };
    if (actions[event.key]) {
      event.preventDefault();
      actions[event.key]();
      if (event.key.startsWith('Arrow') && target.closest('.dialog-thumbnails-list')) {
        this.refs.thumbnails.querySelectorAll('button')[this.#index]?.focus({ preventScroll: true });
      }
    }
  }

  previous() {
    this.selectThumbnail((this.#index - 1 + this.refs.media.length) % this.refs.media.length);
  }

  next() {
    this.selectThumbnail((this.#index + 1) % this.refs.media.length);
  }

  #step = (event) => {
    if (event.target !== this.#wrapper) return;
    event.detail.direction > 0 ? this.next() : this.previous();
  };

  zoomIn() {
    this.#wrapper?.zoomBy?.(0.5);
  }

  zoomOut() {
    this.#wrapper?.zoomBy?.(-0.5);
  }

  resetZoom() {
    this.#wrapper?.reset?.();
  }

  #updateControls = () => {
    const scale = this.#wrapper?.scale ?? 1;
    const controls = this.querySelector('[data-gallery-zoom-controls]');
    if (controls) controls.hidden = !this.#wrapper;
    const minus = this.querySelector('[data-gallery-minus]');
    const plus = this.querySelector('[data-gallery-plus]');
    if (minus) minus.disabled = scale <= 1;
    if (plus) plus.disabled = scale >= 4;
    const value = this.querySelector('[data-gallery-scale]');
    if (value) value.textContent = `${Math.round(scale * 100)} %`;
  };

  handleThumbnailClick(index) {
    this.selectThumbnail(Number(index));
  }

  handleThumbnailPointerEnter(index) {
    const media = this.refs.media[Number(index)];
    if (media) this.loadHighResolutionImage(media);
  }

  selectThumbnail(index) {
    const { media, thumbnails } = this.refs;
    if (!Number.isInteger(index) || !media[index]) return;
    this.#pauseMedia();
    this.#wrapper?.reset?.();
    this.#index = index;
    media.forEach((item, i) => {
      item.hidden = i !== index;
      item.setAttribute('aria-hidden', String(i !== index));
    });
    const buttons = thumbnails.querySelectorAll('button');
    buttons.forEach((button, i) => {
      button.setAttribute('aria-current', String(i === index));
      button.removeAttribute('aria-selected');
    });
    this.#scrollThumbnail();
    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${index + 1} / ${media.length}`;
    this.querySelectorAll('[data-gallery-step]').forEach((button) => { button.hidden = media.length < 2; });
    this.#wrapper?.reset?.();
    this.#updateControls();
    this.loadHighResolutionImage(media[index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  #scrollThumbnail() {
    if (!this.refs.dialog?.open) return;
    const thumbnails = this.refs.thumbnails;
    const selected = thumbnails.querySelectorAll('button')[this.#index];
    if (selected) {
      thumbnails.scrollTo({
        left: selected.offsetLeft - (thumbnails.clientWidth - selected.offsetWidth) / 2,
        behavior: 'instant',
      });
    }
  }

  loadHighResolutionImage(mediaContainer) {
    const image = mediaContainer?.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;
    const url = image.getAttribute('data_max_resolution');
    if (!url || this.#highResImagesLoaded.has(url)) return;
    const loader = new Image();
    this.#highResImagesLoaded.add(url);
    loader.onload = () => {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = url;
    };
    loader.onerror = () => this.#highResImagesLoaded.delete(url);
    loader.src = url;
  }
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
